import Link from 'next/link';
import { IoArrowForward } from 'react-icons/io5';

import { Anchor, Box, Group, Progress, Stack, Text } from '@mantine/core';

import { PermissionTeam, SubscriptionType, Team } from 'models';
import { CreditHelpers } from 'helpers/CreditHelpers';
import { IntlHelpers } from 'helpers/IntlHelpers';
import { SubscriptionConfigHelpers } from 'helpers/SubscriptionConfigHelpers';
import { SubscriptionHelpers } from 'helpers/SubscriptionHelpers';
import { TeamHelpers } from 'helpers/TeamHelpers';

import { SubscriptionOnboardingButton } from 'components/subscriptions/SubscriptionOnboardingButton';
import { useCreditBalanceDetails } from 'hooks/use-credit-balance-details';
import { useCurrentUser } from 'hooks/use-current-user';

import cx from './Dashboard.module.scss';

const DashboardPlan = ({ team }: { team: Team }) => {
    const user = useCurrentUser();
    const canManageBilling = TeamHelpers.hasUserPermission(team, user, [PermissionTeam.ADMIN, PermissionTeam.OWNER]);

    const { subscription } = SubscriptionHelpers.getSubscriptionData(SubscriptionType.DESIGNER, team.subscriptions);
    const { title } = SubscriptionConfigHelpers.getSubscriptionConfig(subscription);
    const isFree = SubscriptionHelpers.isFreeSubscription(subscription);

    const creditsHidden = CreditHelpers.hidden(team);
    const { creditBalanceInfo, isLoading } = useCreditBalanceDetails(creditsHidden ? null : team);

    if (isLoading) {
        return null;
    }

    return (
        <Box className={cx.heroPlan}>
            <Stack gap={2}>
                <Text size="sm" c="gray.3">
                    {title} plan
                </Text>
                {creditBalanceInfo && <CreditBalance {...creditBalanceInfo} />}
            </Stack>

            {canManageBilling && (
                <Group gap="md" wrap="nowrap">
                    {isFree && (
                        <SubscriptionOnboardingButton
                            size="compact-md"
                            radius="xl"
                            variant="white"
                            leftSection={null}
                            rightSection={<IoArrowForward />}
                        />
                    )}
                    {!isFree && creditBalanceInfo && (
                        <Anchor
                            component={Link}
                            href={TeamHelpers.urls.creditUsage(team.id)}
                            c="white"
                            fz="sm"
                            fw={500}
                        >
                            Manage credits
                        </Anchor>
                    )}
                </Group>
            )}
        </Box>
    );
};

const CreditBalance = ({
    balance,
    monthlyUsage,
    remainingPercent,
    color,
}: {
    balance: number;
    monthlyUsage: number;
    remainingPercent: number;
    color: string;
}) => (
    <Stack gap={8}>
        <Group gap={6} align="baseline" wrap="nowrap">
            <Text fz={28} fw={700} lh={1.1} c="white">
                {IntlHelpers.formatNumber(balance)}
            </Text>
            <Text size="sm" c="gray.3">
                credits left
            </Text>
        </Group>
        <Progress value={remainingPercent} color={color} size="sm" radius="xl" bg="rgba(255, 255, 255, 0.2)" />
        <Text size="xs" c="gray.4">
            {monthlyUsage ? `${IntlHelpers.formatNumber(monthlyUsage)} used this month` : 'None used this month'}
        </Text>
    </Stack>
);

export { DashboardPlan };
